<template>
    <Card :title="$t('labels.filter')">
        <div class="card-body">
            <div class="row">
                <div class="col-lg-4">
                    <FormGroup :label="$t('filterCard.labels.source_filter')">
                        <AppSelect
                            @update:model-value="updateSourceFilter"
                            :model-value="sourceFilter.key"
                            :selected-value="sourceFilter.label"
                            :options="source_filter_options"
                            :selected-values="(marketplaces.includes(integration.service) && category?.source_id) ? [] : [FilterSourceEnum.SERVICE]"
                            option-key="key"
                            option-value="label"
                        />
                    </FormGroup>
                </div>
                <div class="col-lg-4" v-for="languageAlias in integrationLanguages">
                    <FormGroup :label="`${$t('labels.name')} (${$t(`languages.${languageAlias}`)})`" required>
                        <Autocomplete
                            v-if="sourceFilter.key === FilterSourceEnum.SERVICE"
                            @update:model-value="newFilter => selectFilter(newFilter, languageAlias)"
                            :model-value="filter.names[languageAlias]"
                            :selected-value="filter.names[languageAlias]"
                            :url="`/projects/${$route.params.projectUuid}/marketplace/sources/${integration.service}/categories/${extractCategorySourceId()}/attributes`"
                            :query-params="autocompleteQueryParams()"
                            option-key="source_id"
                            :option-value="`name_${languageAlias}`"
                            :not-found-alert-texts="{ category: $t('alerts.marketplace_category_not_found') }"
                        />
                        <Input
                            v-else
                            :model-value="filter.names?.[languageAlias]"
                            @update:model-value="(name) => filter.names[languageAlias] = name"
                        />
                    </FormGroup>
                </div>
                <div class="col-lg-3">
                    <FormGroup :label="$t('labels.type')" required>
                        <AppSelect
                            @update:model-value="type = $event"
                            :model-value="type?.value"
                            :selected-value="type?.label"
                            :options="typesOptions"
                            option-key="value"
                            option-value="label"
                            :disabled="sourceFilter.key === FilterSourceEnum.SERVICE"
                        />
                    </FormGroup>
                </div>
                <div class="col-lg-3">
                    <FormGroup :label="$t('labels.min_value')">
                        <Input
                            type="number"
                            v-model="minNumberValue"
                            :disabled="(sourceFilter.key === FilterSourceEnum.SERVICE) || type?.value !== 'number'"
                        />
                    </FormGroup>
                </div>
                <div class="col-lg-3">
                    <FormGroup :label="$t('labels.max_value')">
                        <Input
                            type="number"
                            v-model="maxNumberValue"
                            :disabled="(sourceFilter.key === FilterSourceEnum.SERVICE) || type?.value !== 'number'"
                        />
                    </FormGroup>
                </div>
                <div class="col-lg-3">
                    <FormGroup :label="$t('labels.measurement')">
                        <Input
                            v-model="measurement"
                            :disabled="sourceFilter.key === FilterSourceEnum.SERVICE"
                        />
                    </FormGroup>
                </div>
                <div class="col-lg-12">
                    <FormGroup :label="$t('labels.ai_description')">
                        <Input
                            v-model="ai_description"
                            :maxLength="255"
                        />
                    </FormGroup>
                </div>
                <div class="col-lg-3">
                    <FormGroup :label="$t('labels.required')">
                        <AppCheckbox
                            v-model="isRequired"
                            :disabled="sourceFilter.key === FilterSourceEnum.SERVICE"
                        />
                    </FormGroup>
                </div>
                <div class="col-lg-3">
                    <FormGroup :label="$t('labels.custom_values')">
                        <AppCheckbox
                            v-model="allowCustomValues"
                            disabled
                        />
                    </FormGroup>
                </div>
                <div class="col-lg-3">
                    <FormGroup :label="$t('labels.is_multiple')">
                        <AppCheckbox v-model="isMultiple" :disabled="sourceFilter.key === FilterSourceEnum.SERVICE"/>
                    </FormGroup>
                </div>
                <div class="col-lg-3">
                    <FormGroup :label="$t('labels.ai_use_in_product_description')">
                        <AppCheckbox v-model="ai_use_in_product_description"/>
                    </FormGroup>
                </div>
            </div>
        </div>
        <div class="card-footer d-flex justify-content-end">
            <Button color="indigo" @click="create" :disabled="!type || Object.keys(filter.names).length === 0">
                {{ $t('buttons.create') }}
            </Button>
        </div>
    </Card>
</template>
<script>
import Card from "../../../../components/new/CardComponent.vue";
import FormGroup from "../../../../components/new/form/group/FormGroup.vue";
import Input from "../../../../components/new/form/Input.vue";
import Button from "../../../../components/new/button/AppButton.vue";
import Autocomplete from "../../../../components/new/form/autocomplete/AppAutoComplete.vue";
import { FilterSourceEnum } from '../../../enums/FilterSourceEnum';
import AppSelect from "../../../../components/new/form/select/AppSelect.vue";
import AppCheckbox from "../../../../components/new/form/checkbox/AppCheckbox.vue";

import { httpClient } from "../../../../services/HttpClient";
import MessageNotificator from "../../../../services/MessageNotificator";
import FilterTypes from "../../../enums/FilterTypesEnum";
import { getCommonMarketplaces } from '../../../enums/CommonMarketplaceEnum';

export default {
    name: "CreateFilterCard",
    components: {
        Card, FormGroup, Input, Button, Autocomplete, AppSelect, AppCheckbox
    },
    props: {
        integration: {
            type: Object
        }
    },
    data() {
        return {
            FilterSourceEnum,
            typesOptions: Object.values(FilterTypes).map(type => ({value: type, label: this.$t(`filter_types.${type}`)})),
            filter: {
                names: {}
            },
            isMultiple: false,
            type: {
                value: 'string',
                label: this.$t(`filter_types.string`)
            },
            sourceId: null,
            category: null,
            allowCustomValues: true,
            isRequired: false,
            minNumberValue: null,
            maxNumberValue: null,
            integrationLanguages: ["uk", "ru"],
            sourceFilter: {
                key: FilterSourceEnum.CUSTOM,
                label: this.$t('filterCard.labels.custom')
            },
            source_filter_options: [
                {key: FilterSourceEnum.CUSTOM, label: this.$t('filterCard.labels.custom')},
                {key: FilterSourceEnum.SERVICE, label: this.$t('filterCard.labels.from_service')}
            ],
            measurement: null,
            ai_use_in_product_description: false,
            ai_description: null,
            marketplaces: getCommonMarketplaces(),
        }
    },
    methods: {
        extractCategorySourceId() {
            if (!this.category?.source_id) {
                return null;
            }

            if (String(this.category.source_id).includes('-')) {
                return Number(String(this.category.source_id).split('-')[0]);
            }

            return this.category.source_id;
        },
        updateSourceFilter(sourceFilter) {
            this.sourceFilter = sourceFilter;
            this.filter = {
                names: {}
            };
            this.sourceId = null;
            this.type = {
                value: 'string',
                label: this.$t(`filter_types.string`)
            };
            this.measurement = null;
            this.minNumberValue = null;
            this.maxNumberValue = null;
            this.allowCustomValues = true;
            this.isRequired = false;
            this.isMultiple = false;
        },
        async create() {
            let requestData = {
                names: this.filter.names,
                integration_category_uuid: this.$route.params.categoryUuid,
                type: this.type.value,
                allow_custom_values: this.allowCustomValues,
                is_multiple: this.isMultiple,
                ai_description: this.ai_description,
                ai_use_in_product_description: this.ai_use_in_product_description
            }

            if (this.sourceFilter.key === FilterSourceEnum.SERVICE && this.marketplaces.includes(this.integration.service)) {
                requestData.source_id = this.sourceId
            }

            if (this.isRequired) {
                requestData.is_required = this.isRequired
            }

            if (this.type.value === 'number' && this.minNumberValue !== null) {
                requestData.min_number_value = this.minNumberValue
            }

            if (this.type.value === 'number' && this.maxNumberValue !== null) {
                requestData.max_number_value = this.maxNumberValue
            }

            if (this.measurement) {
                requestData.measurement_unit = this.measurement
            }

            await httpClient.post(`/projects/${this.$route.params.projectUuid}/pim/channels/${this.$route.params.integrationUuid}/filters`, requestData).then(() => {
                this.$router.replace({
                    name: "EditCategoryCard"
                })
                new MessageNotificator().success(this.$t("alerts.success"))
            })
        },
        async fetchCategory() {
            await httpClient.get(`/projects/${this.$route.params.projectUuid}/pim/channels/${this.$route.params.integrationUuid}/categories/${this.$route.params.categoryUuid}`).then((response) => {
                this.category = response.data.data
            })
        },
        selectFilter(filter) {
            this.integrationLanguages.forEach(lang => {
                if (!filter[`name_${lang}`]) {
                    return
                }
                
                this.filter.names[lang] = filter[`name_${lang}`]
            })

            this.sourceId = filter.source_id
            this.type = {
                value: filter.type,
                label: this.$t(`filter_types.${filter.type}`)
            }

            if (filter.type === 'number') {
                this.minNumberValue = filter.min_number_value
                this.maxNumberValue = filter.max_number_value
            } else {
                this.minNumberValue = null
                this.maxNumberValue = null
            }
       
            this.measurement = filter.measurement_unit
            this.allowCustomValues = filter.allow_custom_values
            this.isRequired = filter.is_required
            this.isMultiple = filter.is_multiple
        },
        autocompleteQueryParams() {
            if (!this.category) return

            let queryParams = {
                category_source_id: this.extractCategorySourceId(),
                orders: [
                    {
                        column: 'id',
                        dir: 'asc'
                    }
                ]
            }

            if (this.integration.service === 'kasta') {
                queryParams.category_parent_id = this.category.parent_source_id
            }

            return queryParams
        }
    },
    mounted() {
        this.fetchCategory()
    }
}
</script>
